<script setup>
/**
 * 新增問題元件 (AskNewQus.vue)
 *
 * 提供標題、分類與 Quill 編輯器內容的表單，送出後將 Delta 轉為 HTML 並呼叫 API 新增問題。
 * 標題出現「繳交 / 提交 / 交作業」等關鍵字時，自動切換分類為作業繳交。
 *
 * @fires changeView - 送出成功或返回列表
 */
import questionsAPI from '../apis/questions';
import {
  ref, onMounted, watchEffect, defineEmits
} from 'vue';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import { QuillDeltaToHtmlConverter } from 'quill-delta-to-html';

// 因為改變 delimiters 出現的問題
// eslint-disable-next-line no-unused-vars
import { questionType } from '../utils/format';
// eslint-disable-next-line no-unused-vars
import { toolbarOptions, modulesOptions } from '../plugins/quill';
import { useToast } from '../composables/useToast';

const { postMessage } = useToast();

const emit = defineEmits(['changeView']);
const courseTitle = ref('');
const courseId = ref('');
const isLoading = ref(true);

const questionTypesList = ref({});
/**
 * 取得問題分類清單並寫入 `questionTypesList`。
 */
const getQuestionCategoryMenu = () => {
  questionsAPI.apiGetQuestionTypes().then((res) => {
    questionTypesList.value = res.data.question_types;
    isLoading.value = false;
  }).catch((error) => {
    isLoading.value = false;
    postMessage('danger', '取得問題分類時發生錯誤', `${error?.response?.status} - ${error?.response?.data.message}`);
  });
};

const editorContent = ref(null);
const newQuestion = ref({
  title: '',
  question_type_id: 1,
  body: '',
});

/**
 * 限制 `newQuestion` 指定欄位長度，超過則截斷。
 * @param {'title'} key - `newQuestion` 的欄位名稱
 * @param {number} limit - 上限字數
 */
const limitLength = (key, limit) => {
  if (newQuestion.value[key].length > limit) {
    newQuestion.value[key] = newQuestion.value[key].slice(0, limit);
  }
}

watchEffect(() => {
  if (newQuestion.value.title !== '') {
    const tempTitle = newQuestion.value.title || '';
    if (['繳交', '提交', '交作業'].some((word) => tempTitle.match(word))) {
      newQuestion.value.question_type_id = 2;
    }
  }
});
/**
 * 將 Quill 編輯器的 Delta 內容轉為 HTML 字串並寫入 `newQuestion.body`。
 */
const convertDeltaToHTML = () => {
  const cfg = {};
  const converter = new QuillDeltaToHtmlConverter(editorContent.value.ops, cfg);
  newQuestion.value.body = converter.convert();
};
/**
 * 轉換內容後送出新問題；內容超過 16800 字則中止並提示。成功後返回列表並要求刷新。
 */
const submitNewQuestion = () => {
  convertDeltaToHTML();
  isLoading.value = true;
  if (newQuestion.value.body.length > 16800) {
    alert('問題內容過長（限制 16800 字），請刪除部分文字');
    isLoading.value = false;
    return;
  } else {
    questionsAPI.apiPostQuestion(courseId.value, newQuestion.value).then(() => {
      isLoading.value = false;
      emit('changeView', { type: 'list', id: -1, refresh: true });
    }).catch((error) => {
      isLoading.value = false;
      postMessage('danger', '提出新問題時發生錯誤', `${error?.response?.status} - ${error?.response?.data.message}`);
    })
  }
};
onMounted(() => {
  courseTitle.value = document.getElementById('lecture_heading').textContent.trim();
  courseId.value = document.getElementById('lecture_heading').dataset.lectureId;
  getQuestionCategoryMenu();
});
</script>

<template>
  <div class="mt-4">
    <a href="#" class="text-dark text-decoration-none d-flex align-items-center"
      @click.prevent="$emit('changeView', { type: 'list', id: -1, refresh: false })"><span
        class="material-icons align-bottom me-1">
        arrow_back
      </span> 返回問題列表</a>
    <div class="mt-3 position-relative">
      <loading :active="isLoading" :is-full-page="false" />
      <div class="d-flex align-items-center">
        <h4 class="mb-0">
          新增問題
        </h4>
        <p class="mb-0 bg-light text-muted h5 py-2 px-3 rounded-pill ms-2">
          ${courseTitle}
        </p>
      </div>
      <div class="my-3">
        <label for="title" class="form-label">問題標題 <small>(50 字內)</small></label>
        <input id="title" v-model="newQuestion.title" type="text" class="form-control" :class="!newQuestion.title && 'is-invalid'" aria-describedby=""
          @blur="limitLength('title', 50)" @keydown="limitLength('title', 50)" placeholder="e.g. 作業繳交" />
        <div class="invalid-feedback" :class="!newQuestion.title && 'd-block'">
          問題標題為必填
        </div>
      </div>
      <div class="mb-3">
        <label for="category" class="form-label">問題分類</label>
        <select id="category" v-model="newQuestion.question_type_id" class="form-select"
          aria-label="Default select example">
          <option v-for="qusType in questionTypesList" :key="qusType.name" :value="qusType.id">
            ${questionType(qusType.name)}
          </option>
        </select>
      </div>
      <div class="mt-3">
        <QuillEditor v-model:content="editorContent" theme="snow" :toolbar="toolbarOptions" :modules="modulesOptions" />
        <div class="my-3 d-flex justify-content-end">
          <button class="btn btn-hex-primary px-4"
            :disabled="newQuestion.title === '' || !editorContent || (typeof editorContent?.ops?.[0]?.insert === 'string' && editorContent?.ops?.[0]?.insert.trim() === '') || isLoading"
            @click="submitNewQuestion">
            送出
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>

<spec lang="md">
# 新增問題 (AskNewQus.vue)

## 元件概述
- **用途**：提供新增問題表單（標題、分類、Quill 內容），送出後呼叫 API 建立問題。

## Props
無

## Emits
| 事件名稱 | 參數 | 說明 |
|---------|-----|------|
| changeView | `({ type, id, refresh })` | 返回列表或送出成功後導回列表 |

## Slots
無

## 功能行為
- `onMounted` 由 `#lecture_heading` 取得課程標題（`courseTitle`）與 `data-lecture-id`（`courseId`），並載入問題分類
- `watchEffect` 監看標題，含「繳交 / 提交 / 交作業」關鍵字時自動將 `question_type_id` 設為 `2`
- `limitLength` 於 blur / keydown 時限制標題 50 字
- `submitNewQuestion` 先以 `convertDeltaToHTML` 將 Quill Delta 轉 HTML；內容超過 16800 字則 `alert` 中止
- 送出成功後 emit `changeView` 導回列表並帶 `refresh: true`

## 測試行為
- 應顯示「新增問題」標題與課程名稱
- 標題為空時，輸入框應套用 `is-invalid` 並顯示「問題標題為必填」
- 標題含「繳交」等關鍵字時，分類應自動切換為作業繳交（id `2`）
- 標題超過 50 字時，應截斷為 50 字
- 標題為空、編輯器無內容或載入中時，送出按鈕應禁用
- 內容超過 16800 字時，應 `alert` 並中止送出
- 送出成功時，應 emit `changeView` 並帶 `type: 'list'`、`refresh: true`
- 點擊「返回問題列表」時，應 emit `changeView` 並帶 `type: 'list'`

## UI 規格
- 頂部「返回問題列表」連結含 Material Icons `arrow_back`
- 課程名稱以 `.bg-light.rounded-pill` 標籤呈現
- 使用 `QuillEditor`（snow 主題）作為內容編輯器
- 載入時以 `vue-loading-overlay` 顯示區塊級 Loading（`:is-full-page="false"`）
</spec>
